/**
 * HomeSectionNav — a sticky, horizontally-scrollable bookmark bar
 * mounted just below the primary header. Lets visitors jump straight
 * to any homepage section without endlessly scrolling.
 *
 * • IntersectionObserver highlights the currently visible section.
 * • Anchors resolved by matching `id` on the wrapping <section>.
 * • Smooth-scroll respects `prefers-reduced-motion`.
 * • Hidden until the user has scrolled past the hero to keep the
 *   first screen calm.
 */
import { useEffect, useRef, useState } from "react";
import {
  Sparkles, LayoutList, HandHeart, MapPin, Grid3x3, Calendar,
  Award, Home as HomeIcon, Building2, BookOpen,
} from "lucide-react";

// Order MUST match the order the sections are laid out in Home.jsx.
export const HOME_SECTIONS = [
  { id: "highlights",   label: "Right Now",   icon: Sparkles },
  { id: "feed",         label: "Feed",        icon: LayoutList },
  { id: "help",         label: "Help",        icon: HandHeart },
  { id: "local",        label: "Local Pulse", icon: MapPin },
  { id: "network",      label: "Network",     icon: Grid3x3 },
  { id: "events",       label: "Events",      icon: Calendar },
  { id: "changer",      label: "Changer",     icon: Award },
  { id: "housing",      label: "Housing",     icon: HomeIcon },
  { id: "organizations", label: "Orgs",       icon: Building2 },
  { id: "stories",      label: "Stories",     icon: BookOpen },
];

export default function HomeSectionNav() {
  const [active, setActive] = useState("");
  const [visible, setVisible] = useState(false);
  const barRef = useRef(null);

  // Show the bar only after we've scrolled past the hero (~600px).
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track which section is closest to the top of the viewport.
  useEffect(() => {
    const els = HOME_SECTIONS
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);
    if (els.length === 0) return;

    const obs = new IntersectionObserver(
      (entries) => {
        // Prefer whichever section is most visible right now.
        const visibleEntries = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visibleEntries[0]?.target?.id) {
          setActive(visibleEntries[0].target.id);
        }
      },
      { rootMargin: "-120px 0px -60% 0px", threshold: [0.1, 0.25, 0.5] }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // Auto-scroll the active chip into view within the horizontal bar.
  useEffect(() => {
    if (!active || !barRef.current) return;
    const btn = barRef.current.querySelector(`[data-anchor="${active}"]`);
    if (btn && typeof btn.scrollIntoView === "function") {
      btn.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [active]);

  const jump = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const top = el.getBoundingClientRect().top + window.scrollY - 88; // header offset
    window.scrollTo({ top, behavior: reduced ? "auto" : "smooth" });
  };

  return (
    <div
      className={`sticky top-0 z-30 transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"
      }`}
      data-testid="home-section-nav"
      role="navigation"
      aria-label="Homepage sections"
    >
      <div
        className="w-full backdrop-blur-md"
        style={{ background: "rgba(253,251,247,0.9)", borderBottom: "1px solid #EEE6D6" }}
      >
        <div
          ref={barRef}
          className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-10 py-2 flex items-center gap-1.5 overflow-x-auto no-scrollbar"
        >
          {HOME_SECTIONS.map((s) => {
            const isActive = active === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => jump(s.id)}
                data-anchor={s.id}
                data-testid={`home-section-nav-${s.id}`}
                aria-current={isActive ? "true" : undefined}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  isActive
                    ? "bg-[#1E64C8] text-white shadow-sm"
                    : "text-[#0B1226] hover:bg-white hover:shadow-sm"
                }`}
              >
                <s.icon className="w-3.5 h-3.5" />
                <span>{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
